import { SNAKE_CONFIG } from './snakeConfig.js';

const {
    ADVENTURE_SCREENS,
    ADVENTURE_POINTS_TO_UNLOCK,
    ADVENTURE_DOOR_SIDES,
    ADVENTURE_HOUSE_W,
    ADVENTURE_HOUSE_H,
} = SNAKE_CONFIG;

/**
 * State manager for Snake Adventure mode.
 * Tracks current screen (0-3), per-screen score,
 * door/house unlock state, and progression.
 */
export class SnakeAdventure {
    currentScreen = 0;
    screenScore = 0;
    totalScore = 0;
    totalFood = 0;
    doorUnlocked = false;
    houseOpen = false;
    _pointsToUnlock = ADVENTURE_POINTS_TO_UNLOCK;

    /**
     * @param {number} [pointsToUnlock] - Override for points needed per screen
     */
    constructor(pointsToUnlock) {
        if (pointsToUnlock && pointsToUnlock > 0) {
            this._pointsToUnlock = pointsToUnlock;
        }
    }

    get isLastScreen() {
        return this.currentScreen === ADVENTURE_SCREENS - 1;
    }

    get pointsToUnlock() {
        return this._pointsToUnlock;
    }

    get pointsNeeded() {
        return Math.max(0, this._pointsToUnlock - this.screenScore);
    }

    doorSideForScreen(idx) {
        if (idx < 0 || idx >= ADVENTURE_SCREENS) return null;
        return ADVENTURE_DOOR_SIDES[idx];
    }

    get currentDoorSide() {
        return this.doorSideForScreen(this.currentScreen);
    }

    /**
     * Place the house on the final screen (screen 3).
     * Returns { col, row } for the top-left corner of the 2x2 house,
     * positioned far from the entry side and away from obstacles.
     */
    pickHousePosition(cols, rows, excludedSet) {
        const margin = 3;
        const maxAttempts = 200;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const col = margin + Math.floor(Math.random() * (cols - ADVENTURE_HOUSE_W - margin * 2));
            const row = margin + Math.floor(Math.random() * (rows - ADVENTURE_HOUSE_H - margin * 2));

            let blocked = false;
            for (let dc = -1; dc <= ADVENTURE_HOUSE_W; dc++) {
                for (let dr = -1; dr <= ADVENTURE_HOUSE_H; dr++) {
                    if (excludedSet.has(`${col + dc},${row + dr}`)) {
                        blocked = true;
                        break;
                    }
                }
                if (blocked) break;
            }

            if (!blocked) return { col, row };
        }

        return { col: Math.floor(cols / 2), row: Math.floor(rows / 2) };
    }

    /**
     * Returns all grid cells occupied by the house.
     */
    getHouseCells(housePos) {
        const cells = [];
        for (let dc = 0; dc < ADVENTURE_HOUSE_W; dc++) {
            for (let dr = 0; dr < ADVENTURE_HOUSE_H; dr++) {
                cells.push({ col: housePos.col + dc, row: housePos.row + dr });
            }
        }
        return cells;
    }

    addScore(points) {
        this.screenScore += points;
        this.totalScore += points;

        if (this.screenScore >= this._pointsToUnlock) {
            if (this.isLastScreen) {
                this.houseOpen = true;
            } else {
                this.doorUnlocked = true;
            }
            return true;
        }
        return false;
    }

    addFood(amount = 1) {
        this.totalFood += amount;
    }

    /**
     * Advance to the next screen. Returns false if already on the last screen.
     */
    advanceScreen() {
        if (this.currentScreen >= ADVENTURE_SCREENS - 1) return false;
        this.currentScreen++;
        this.screenScore = 0;
        this.doorUnlocked = false;
        this.houseOpen = false;
        return true;
    }

    reset() {
        this.currentScreen = 0;
        this.screenScore = 0;
        this.totalScore = 0;
        this.totalFood = 0;
        this.doorUnlocked = false;
        this.houseOpen = false;
    }
}
